import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, BrowserRouter, useLocation } from "react-router-dom";
import routes from "./Consts/routes";
import RouteProtector from "./Components/Shared/General/RouteProtector";
import "./App.less";
import { ConfigProvider, App as AntdApp, Spin, locale, SiteBannerStrip, type PublicSiteBanner } from "@version2/ui-package";
import { EnvironmentTag } from "@zektec/environment-tag";
import themes from "./Consts/theme";
import { GTMProvider } from "@elgorditosalsero/react-gtm-hook";
import { env } from "./Consts/config";
import { Env } from "@version2/domain-package";
import { useDispatch } from "react-redux";
import { enableMapSet } from "immer";
import { HelmetProvider } from "react-helmet-async";
import AntdMessageContextBridge from "./Components/Shared/Providers/AntdMessageContextBridge";
import AppErrorBoundary from "./Components/Shared/Providers/AppErrorBoundary";
import { useFeatureAccess } from "./Services/feature_access";
import FeatureNotAvailable from "./Views/FeatureNotAvailable";
import { addSentryRouteBreadcrumb } from "./Services/sentry";
import { apiGet } from "./Services/api/client";

type IdleCleanup = () => void;

type WindowWithIdle = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

const scheduleLazyInitializer = (
  initializer: () => void | Promise<void>
): IdleCleanup | undefined => {
  if (typeof window === "undefined") {
    void initializer();
    return undefined;
  }

  const { requestIdleCallback, cancelIdleCallback } = window as WindowWithIdle;

  if (requestIdleCallback) {
    const handle = requestIdleCallback(() => {
      void initializer();
    });
    return () => cancelIdleCallback?.(handle);
  }

  const timeoutId = window.setTimeout(() => {
    void initializer();
  }, 0);
  return () => window.clearTimeout(timeoutId);
};

const isBrowserEnvironment = typeof window !== "undefined";

if (!isBrowserEnvironment) {
  enableMapSet();
}

const App = () => {
  const gtmParams = { id: env.GOOGLE_TAG_MANAGER_ID };
  const dispatch = useDispatch();
  const { isFeatureEnabled } = useFeatureAccess();
  const baseLightTheme = themes["light"] as { token?: Record<string, unknown> };
  const baseToken = baseLightTheme.token || {};
  const lightThemeConfig = {
    ...baseLightTheme,
    token: {
      ...baseToken,
      colorPrimary: "#07988f",
      colorInfo: "#07988f",
      colorLink: "#07988f",
      colorPrimaryHover: "#0b847d",
      colorInfoHover: "#0b847d",
      colorPrimaryActive: "#066c66",
      colorInfoActive: "#066c66",
    },
  };

  useEffect(() => {
    const cleanupCallbacks: IdleCleanup[] = [];

    if (isBrowserEnvironment) {
      const immerCleanup = scheduleLazyInitializer(enableMapSet);
      if (immerCleanup) cleanupCallbacks.push(immerCleanup);
    }

    const paddleCleanup = scheduleLazyInitializer(async () => {
      const { initPaddle } = await import("./Services/paddle/initPaddle");
      await initPaddle(dispatch);
    });
    if (paddleCleanup) cleanupCallbacks.push(paddleCleanup);

    return () => {
      cleanupCallbacks.forEach((cleanup) => cleanup());
    };
  }, [dispatch]);

  return (
    <HelmetProvider>
      <GTMProvider state={gtmParams}>
        <ConfigProvider locale={locale} theme={lightThemeConfig}>
          <AntdApp>
            <AntdMessageContextBridge>
              <AppErrorBoundary>
                <EnvironmentTag
                  isLocal={env.IS_LOCAL}
                  isStaging={env.ENV === Env.STAGE}
                  placement="bottom-center"
                />
                <BrowserRouter>
                  <AnalyticsListener />
                  <LandingSiteBanners />
                  <Suspense
                    fallback={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "50vh",
                        }}
                      >
                        <Spin size="large" />
                      </div>
                    }
                  >
                    <Routes>
                      {routes.map((route) => (
                        <Route
                          key={route.path}
                          path={route.path}
                          element={
                            <RouteProtector
                              element={
                                isFeatureEnabled(route.featureKey) ? (
                                  route.element || <div />
                                ) : (
                                  <FeatureNotAvailable featureKey={route.featureKey} />
                                )
                              }
                              fallback={route.fallback}
                              isGuarded={route.isGuarded}
                            />
                          }
                        >
                          {(route.routes || []).map((child) => (
                            <Route
                              key={child.path}
                              path={child.path}
                              element={
                                isFeatureEnabled(child.featureKey) ? (
                                  child.element
                                ) : (
                                  <FeatureNotAvailable featureKey={child.featureKey} />
                                )
                              }
                            />
                          ))}
                        </Route>
                      ))}
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </AppErrorBoundary>
            </AntdMessageContextBridge>
          </AntdApp>
        </ConfigProvider>
      </GTMProvider>
    </HelmetProvider>
  );
};

const LandingSiteBanners = () => {
  const [banners, setBanners] = useState<PublicSiteBanner[]>([]);

  useEffect(() => {
    let canceled = false;
    void apiGet("/v1/site-banners?site=landing", false)
      .then((response) => {
        if (!canceled) setBanners(response.success && Array.isArray(response.data?.banners) ? response.data.banners : []);
      })
      .catch(() => { if (!canceled) setBanners([]); });
    return () => { canceled = true; };
  }, []);

  return <SiteBannerStrip banners={banners} />;
};

const AnalyticsListener = () => {
  const location = useLocation();

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    addSentryRouteBreadcrumb(path);
    void (async () => {
      const { trackPageView } = await import("./Services/firebase/analytics");
      await trackPageView(path, {
        referrer: document.referrer || undefined,
      });
    })();
  }, [location.pathname, location.search]);

  return null;
};

export default App;
